import { assetScreenerColumns, COMPARISON_OPTIONS, Sort } from "../models/assetScreener.models"
import { TokenDO } from "../../tokenData/models/tokenDO.models"
import { i18n } from "../../services/i18n.service"

export class AssetScreenerHeaderComponent extends HTMLTableRowElement {
    // Workaround: cmc_rank is the default sort field, not shown in the header, used for default sort
    private static readonly DEFAULT_SORT: Sort = { field: 'cmc_rank', direction: 'asc' }
    private onSortChange: (sort: Sort) => void
    private currentSort: Sort
    
    constructor(onSortChange: (sort: Sort) => void, currentSort: Sort) {
        super()
        this.onSortChange = onSortChange
        this.currentSort = currentSort
        this.classList.add('asset-screener-header')
    }

    connectedCallback() {
        this.init()
    }

    private init() {
        const columns = assetScreenerColumns

        columns.forEach((columnName) => {
            const tHeadRowCell = document.createElement('th')
            const button = document.createElement('button')
            button.classList.add('caption1', 'is-medium', 'sort-button')
            
            // Create container for text and arrow
            const contentContainer = document.createElement('div')
            contentContainer.classList.add('sort-content')
            
            // Add text with localized content
            const textSpan = document.createElement('span')
            textSpan.innerText = i18n.t(`assetScreener.${columnName}`)
            contentContainer.appendChild(textSpan)
            
            // Add arrow container
            const arrowSpan = document.createElement('span')
            arrowSpan.classList.add('sort-arrow')
            contentContainer.appendChild(arrowSpan)
            
            button.appendChild(contentContainer)
            button.addEventListener('click', () => {
                this.toggleSort(columnName)
            })
            tHeadRowCell.appendChild(button)
            this.appendChild(tHeadRowCell)
        })

        this.updateSortIndicators()
    }

    private toggleSort(dataField: string) {
        if (this.currentSort.field === dataField) {
            // Toggle direction or set to default sort
            if (this.currentSort.direction === 'desc') {
                this.currentSort.direction = 'asc'
            } else if (this.currentSort.direction === 'asc') {
                // Set to default sort
                this.currentSort = { ...AssetScreenerHeaderComponent.DEFAULT_SORT }
            }
        } else {
            // New field, start with descending
            this.currentSort = { field: dataField, direction: 'desc' }
        }

        this.updateSortIndicators()
        this.onSortChange(this.currentSort)
    }

    private updateSortIndicators() {
        const buttons = this.querySelectorAll('.sort-button')
        buttons.forEach(button => {
            const arrow = button.querySelector('.sort-arrow')
            if (!arrow) return
            
            const dataField = assetScreenerColumns.find((columnName) => button.textContent?.trim() === i18n.t(`assetScreener.${columnName}`))
            
            if (dataField === this.currentSort.field) {
                arrow.textContent = this.currentSort.direction === 'desc' ? '↓' : '↑'
            } else {
                arrow.textContent = ''
            }
        })
    }

    static sortData(data: TokenDO[], sort: Sort): TokenDO[] {
        if (!sort.field || !sort.direction) {
            return [...data]
        }

        const sortedData = [...data].sort((a, b) => {
            // Determine if we're sorting a string field or a numeric field
            const isStringField = sort.field === 'name'
            const comparisonOptions = isStringField 
                ? COMPARISON_OPTIONS.string
                : COMPARISON_OPTIONS.numeric

            return isStringField
                ? comparisonOptions(a, b, sort.direction)
                : comparisonOptions(a, b, sort.direction, sort.field)
        })

        return sortedData
    }
}

window.customElements.define('asset-screener-header', AssetScreenerHeaderComponent, { extends: 'tr' }) 